import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from 'src/components/Icon';
import styles from './styles.module.scss';

interface ImageViewerProps {
    imageUrl: string;
    alt?: string;
    overlay?: (imageSize: { naturalWidth: number; naturalHeight: number }) => React.ReactNode;
}

function ImageViewer({ imageUrl, alt = 'Document scan', overlay }: ImageViewerProps) {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [imageSize, setImageSize] = useState({ naturalWidth: 0, naturalHeight: 0 });
    const lastMousePos = useRef({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    const handleZoomIn = () => {
        setIsTransitioning(true);
        setZoom((prev) => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setIsTransitioning(true);
        setZoom((prev) => Math.max(prev - 0.25, 0.5));
    };

    const handleRotate = () => {
        setIsTransitioning(true);
        setRotation((prev) => (prev + 90) % 360);
    };

    const handleReset = () => {
        setIsTransitioning(true);
        setZoom(1);
        setRotation(0);
        setPanX(0);
        setPanY(0);
    };

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return; // Only handle left mouse button

        // Ignore events from resize handles
        const target = e.target as HTMLElement;
        if (target.closest('[class*="resizeHandle"]')) {
            return;
        }

        setIsDragging(true);
        setIsTransitioning(false);
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!isDragging) return;

            const deltaX = e.clientX - lastMousePos.current.x;
            const deltaY = e.clientY - lastMousePos.current.y;

            setPanX((prev) => prev + deltaX);
            setPanY((prev) => prev + deltaY);

            lastMousePos.current = { x: e.clientX, y: e.clientY };
            e.preventDefault();
        },
        [isDragging],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (e.touches.length !== 1) return; // Only handle single touch
        setIsDragging(true);
        setIsTransitioning(false);
        const touch = e.touches[0];
        lastMousePos.current = { x: touch.clientX, y: touch.clientY };
        e.preventDefault();
    }, []);

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!isDragging || e.touches.length !== 1) return;

            const touch = e.touches[0];
            const deltaX = touch.clientX - lastMousePos.current.x;
            const deltaY = touch.clientY - lastMousePos.current.y;

            setPanX((prev) => prev + deltaX);
            setPanY((prev) => prev + deltaY);

            lastMousePos.current = { x: touch.clientX, y: touch.clientY };
            e.preventDefault();
        },
        [isDragging],
    );

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleWheel = useCallback(
        (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // Determine zoom direction based on wheel delta
            const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
            const currentZoom = zoom;
            const newZoom = Math.max(0.5, Math.min(3, currentZoom * zoomFactor));

            if (newZoom === currentZoom) return; // Already at min/max zoom

            // Get mouse position relative to container
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;

                // Calculate the point in the image coordinate system before zoom
                const containerCenterX = rect.width / 2;
                const containerCenterY = rect.height / 2;

                // Adjust pan to zoom towards the mouse position
                const zoomChange = newZoom - currentZoom;
                const scaleFactor = zoomChange / currentZoom;

                // Calculate the offset from center
                const offsetX = mouseX - containerCenterX;
                const offsetY = mouseY - containerCenterY;

                // Adjust pan to keep the point under the mouse in the same place
                setPanX((prev) => prev - offsetX * scaleFactor);
                setPanY((prev) => prev - offsetY * scaleFactor);
            }

            setIsTransitioning(false);
            setZoom(newZoom);
        },
        [zoom],
    );

    // Set up native wheel event listener with passive: false to allow preventDefault
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel]);

    useEffect(() => {
        if (isTransitioning) {
            const timer = setTimeout(() => setIsTransitioning(false), 200);
            return () => clearTimeout(timer);
        }
    }, [isTransitioning]);

    const stageStyle = {
        transform: `translate(${panX}px, ${panY}px) scale(${zoom}) rotate(${rotation}deg)`,
        transformOrigin: 'center center',
        transition: isTransitioning ? 'transform 0.2s ease-in-out' : 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <div className={styles.imageViewerWrapper}>
            <div
                ref={containerRef}
                className={styles.assetImageContainer}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className={styles.imageStage} style={stageStyle}>
                    <img
                        src={imageUrl}
                        alt={alt}
                        className={styles.assetImage}
                        draggable={false}
                        onLoad={(event) => {
                            setImageSize({
                                naturalWidth: event.currentTarget.naturalWidth,
                                naturalHeight: event.currentTarget.naturalHeight,
                            });
                        }}
                    />
                    {overlay && imageSize.naturalWidth > 0 && imageSize.naturalHeight > 0 && (
                        <div className={styles.overlayLayer}>
                            {overlay(imageSize)}
                        </div>
                    )}
                </div>
                <div className={styles.imageControls}>
                    <button onClick={handleZoomOut} className={styles.controlButton} aria-label="Zoom Out">
                        <Icon name="search-minus" type="fa" />
                    </button>
                    <button onClick={handleZoomIn} className={styles.controlButton} aria-label="Zoom In">
                        <Icon name="search-plus" type="fa" />
                    </button>
                    <button onClick={handleRotate} className={styles.controlButton} aria-label="Rotate">
                        <Icon name="redo" type="fa" />
                    </button>
                    <button onClick={handleReset} className={styles.controlButton} aria-label="Reset">
                        <Icon name="undo" type="fa" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ImageViewer;
